import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';
import { Users, Server, FileText, Puzzle, Shield, Activity, Wrench, PenSquare } from 'lucide-react';

export default function AdminOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/dashboard');
      return;
    }

    api.getAdminStats()
      .then(setStats)
      .catch(err => console.error('Failed to load admin stats:', err))
      .finally(() => setLoading(false));
  }, [isSuperuser, navigate]);

  if (!isSuperuser) return null;

  const adminCards = [
    { to: '/dashboard/admin/blog', icon: PenSquare, label: 'Blog Manager', desc: 'Create and manage blog posts', color: 'emerald' },
    { to: '/dashboard/admin/status', icon: Activity, label: 'System Status', desc: 'Monitor service health', color: 'cyan' },
    { to: '/dashboard/admin/config', icon: Wrench, label: 'Platform Config', desc: 'Manage platform settings', color: 'amber' },
    { to: '/dashboard/admin/analytics', icon: Users, label: 'Platform Analytics', desc: 'View system-wide stats', color: 'purple' },
  ];

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="flex items-center gap-3">
        <Shield className="text-amber-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Admin Panel</h1>
          <p className="text-sm text-zinc-500">Superuser management dashboard</p>
        </div>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4 animate-pulse">
              <div className="h-4 bg-zinc-800 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-zinc-800 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-zinc-500 uppercase">Total Users</span>
              <Users size={16} className="text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-white font-mono">{stats.users?.total || 0}</p>
            <p className="text-xs text-zinc-500">{stats.users?.active || 0} active</p>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-zinc-500 uppercase">Instances</span>
              <Server size={16} className="text-cyan-500" />
            </div>
            <p className="text-2xl font-bold text-white font-mono">{stats.instances?.total || 0}</p>
            <p className="text-xs text-zinc-500">{stats.instances?.running || 0} running</p>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-zinc-500 uppercase">Blog Posts</span>
              <FileText size={16} className="text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-white font-mono">{stats.blog?.total || 0}</p>
            <p className="text-xs text-zinc-500">{stats.blog?.published || 0} published</p>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-zinc-500 uppercase">Skills</span>
              <Puzzle size={16} className="text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-white font-mono">{stats.skills?.total || 0}</p>
            <p className="text-xs text-zinc-500">in marketplace</p>
          </div>
        </div>
      )}

      {/* Admin Actions */}
      <div>
        <h2 className="text-lg font-bold text-white font-mono mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {adminCards.map(card => (
            <Link
              key={card.to}
              to={card.to}
              data-testid={`admin-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
              className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 hover:border-zinc-700 transition-all group"
            >
              <card.icon size={24} className={`text-${card.color}-500 mb-3`} />
              <h3 className="text-white font-mono font-medium mb-1 group-hover:text-emerald-400 transition-colors">{card.label}</h3>
              <p className="text-xs text-zinc-500">{card.desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Shield className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-amber-200 text-sm font-mono font-medium">Superuser Access</p>
            <p className="text-amber-200/70 text-xs mt-1">
              You have elevated privileges. Changes made in admin panel affect all users and platform functionality.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
